<script setup>
import { excerpt } from '/app/utils/helpers';
import { api } from '/app/plugins/services';
import { config } from '/app/config';
import { ref, computed, inject } from 'vue';
import { useFormContext } from '/app/composables/form';
import SkipButton from './SkipButton.vue';
import normalizeUrl from 'normalize-url';
import axios from 'axios';
import NIcon from '/app/components/NIcon.vue';
import NImage from '/app/components/NImage.vue';
import NInput from '/app/components/form/Input.vue';

const props = defineProps({
	allowReplaceChallengeImage: {
		type: Boolean,
		default: true,
	},
});

const form = useFormContext();
const stepper = inject('stepper', null);

const busy = ref(false);
const url = ref(form?.fields?.challenge?.article ? form.fields.challenge.article.url : '');
const emptyImage = ref(false);
const errorMessage = ref(null);

const isDailyNews = computed(() => false);

const normalizedURL = computed(() => normalizeUrl(url.value));

const hasArticle = computed(() => !!form?.fields?.challenge?.article);

const isValidUrl = computed(() =>
	/^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/.test(url.value)
);

const canUseChallengeImage = computed(() => {
	if (!form?.fields?.challenge?.primary_image) {
		return false;
	}
	if (hasArticle.value) {
		return form.fields.challenge.article.image_url !== form.fields.challenge.primary_image;
	}
	return false;
});

const canReplaceChallengeImage = computed(() => {
	if (emptyImage.value) {
		return false;
	}
	if (hasArticle.value) {
		return (
			form.fields.challenge.article.image_url &&
			form.fields.challenge.article.image_url !== form.fields.challenge.primary_image
		);
	}
	return false;
});

async function scrape() {
	if (form) {
		form.fields.challenge.article = null;
	}

	if (!url.value || !isValidUrl.value) {
		errorMessage.value = 'The provided URL is either missing or invalid. Please enter a valid URL.';
		return;
	}

	try {
		busy.value = true;
		errorMessage.value = null;

		const { data } = await api.tools.scrapeArticle({
			uri: normalizedURL.value,
		});
		const { title, description, image_url, url: articleUrl, keywords } = data;

		if (form) {
			form.fields.challenge.article = {
				title,
				description,
				image_url: null,
				url: articleUrl,
				keywords,
			};
		}

		emptyImage.value = !image_url;

		if (!emptyImage.value) {
			await storeImage(image_url);
		}
	} catch (err) {
		emptyImage.value = true;
		errorMessage.value =
			'Unable to fetch metadata to the provided URL. Please check the URL and try again.';
	} finally {
		busy.value = false;
	}
}

async function storeImage(imageUrl) {
	try {
		const { data } = await axios.get(
			`${config.functions.baseURL}/use-image?source=${imageUrl}`,
			{ responseType: 'arraybuffer' }
		);

		const { data: { url: uploadedUrl } } = await api.resource.uploadImage(
			data,
			{ type: 'png' },
			{}
		);

		if (form) {
			if (!form.fields.challenge.primary_image) {
				form.fields.challenge.primary_image = uploadedUrl;
			}
			form.fields.challenge.article.image_url = uploadedUrl;
		}
	} catch (err) {
		emptyImage.value = true;
	}
}

function skip() {
	if (form) {
		form.fields.article_skiped = true;
	}
	stepper?.next?.();
}

function clear() {
	if (form) {
		form.fields.challenge.article = null;
	}
	url.value = null;
}

function useChallengeImage() {
	if (form?.fields?.challenge?.article) {
		form.fields.challenge.article.image_url = form.fields.challenge.primary_image;
	}
	emptyImage.value = false;
}

function replaceChallengeImage() {
	if (form?.fields?.challenge?.article) {
		form.fields.challenge.primary_image = form.fields.challenge.article.image_url;
	}
}
</script>

<template>
	<div>
		<div class="challenge-section-heading has-optional-tag">
			<div>
				<h2 class="challenge-section-heading__title">
					<b>Enter your News Article's link here</b>
				</h2>
				<p class="challenge-section-heading__description">
					You can link to an interesting or supporting article. A summary of the article will appear inside your challenge detail view.
				</p>
			</div>
			<SkipButton />
		</div>
		<div class="form-section-wrapper">
			<div class="form-groups">
				<div class="form-group is-inline mb-4" @keydown.enter="scrape">
					<NInput placeholder="Paste in the full URL" v-model="url" name="article-url" id="article-url" aria-label="Article URL" >
						<template #prepend>
							<NIcon as="link-solid" class="mr-4" />
						</template>
					</NInput>
					<button aria-label="Retrieve" :disabled="busy || !isValidUrl" type="button" class="flex items-center py-2 px-6 leading-snug rounded-md hover:bg-dust-600 shadow border bg-dust-500 text-white transition ease-in-out duration-300 cursor-pointer" @click.prevent="scrape">
						{{ busy ? 'Retrieving' : 'Retrieve' }}
						<svg v-if="busy" class="animate-spin h-5 w-5 ml-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
							<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
							<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
						</svg>
					</button>
				</div>

				<span v-if="errorMessage" class="form-group__error">{{ errorMessage }}</span>

				<p class="form-block-help-text mb-6 mt-0 px-1">
					<i>We'll automatically pull in the story's title, image and the intro paragraph (when possible). If necessary you can customize the image in the next step.</i>
				</p>
				<div v-if="hasArticle" class="news-item-wrapper is-center mb-8">
					<h3 v-show="isDailyNews" class="form-group__title">
						<span aria-hidden="true" class="icon icon-link-solid"></span>
						Daily News topic
					</h3>
					<div class="news-item shadow-high rounded-md overflow-hidden mb-6">
						<div class="news-item__image-wrapper min-h-80 flex bg-dust-100">
							<a v-show="isDailyNews" href="#" class="news-item__category">Topic Placeholder</a>
							<NImage
								v-if="!emptyImage"
								:srcs="[form.fields.challenge.article.image_url]"
								class="!aspect-image border-b border-dust-100"
							/>
							<div v-else-if="(emptyImage || !form.fields.challenge.primary_image) && stepper" class="flex flex-col items-center justify-center bg-orange-50 p-20">
								<p class="text-orange-400 mb-3 text-center">The image failed to parse. But don't worry, you can upload your own image, graphic or illustration.</p>
								<button aria-label="Upload your own image" type="button" class="px-4 py-3 leading-none bg-orange-400 hover:bg-orange-500 text-white rounded-md" @click="stepper.to('image')">
									Upload your own image
								</button>
							</div>
							<div v-else class="flex flex-col items-center justify-center p-20 w-full">
								<span class="text-dust-600">No image!</span>
							</div>
						</div>
						<div v-show="isDailyNews" class="news-item__meta">
							<span aria-hidden="true" class="icon icon-newspaper-solid"></span>
							<b class="platform">DailyNerve</b> Solve the News
						</div>
						<div class="news-item__content p-4 bg-white">
							<h3 class="news-item__title">{{ form.fields.challenge.article.title }}</h3>
							<p class="news-item__description text-dust-700">
								{{ excerpt(form.fields.challenge.article.description, 250) }}
							</p>
							<a :href="form.fields.challenge.article.url" class="news-item__link" target="_blank">
								{{ form.fields.challenge.article.url }}
							</a>
							<div v-show="isDailyNews">
								<a :href="form.fields.challenge.article.url" class="btn has-no-style" target="_blank">
									<span aria-hidden="true" class="btn__icon is-left icon-link-solid"></span>
									Read the full story
								</a>
							</div>
						</div>
					</div>
					<div v-show="isDailyNews" class="news-item__footer mt-4 mb-6">
						<span aria-hidden="true" class="icon icon-image-regular"></span>
						<b>Don't like this one? <a href="#">Replace the image here.</a></b>
					</div>
					<div class="flex items-center justify-center gap-2">
						<button aria-label="Remove article" class="flex-none flex items-center px-4 py-2 border rounded-md text-white bg-red-500 hover:bg-red-600" @click="clear">
							<NIcon as="remove" left />
							Remove article
						</button>
						<button aria-label="Use challenge image" v-if="canUseChallengeImage" class="flex-none flex items-center px-4 py-2 border border-nerve-300 rounded-md text-nerve-500 bg-nerve-100 hover:bg-nerve-200 hover:text-nerve-600" @click="useChallengeImage">
							Use challenge image
						</button>
						<button aria-label="Replace challenge image" v-if="canReplaceChallengeImage && allowReplaceChallengeImage" class="flex-none flex items-center px-4 py-2 border border-nerve-300 rounded-md text-nerve-500 bg-nerve-100 hover:bg-nerve-200 hover:text-nerve-600" @click="replaceChallengeImage">
							Replace challenge image
						</button>
					</div>
				</div>
				<div v-else class="flex items-center justify-center px-2 sm:p-10">
					<slot name="placeholder">
						<div class="w-full flex items-center justify-center max-w-140 min-h-[40rem] h-[35rem] border-2 border-dust-300 border-dashed rounded-md">
							<p class="text-dust-600">
								Enter a URL to an article you'd like to share with your challenge participants.
							</p>
						</div>
					</slot>
				</div>
			</div>
		</div>
	</div>
</template>
